/**
 * Per-frame leg position tables for the procedural horse. Each frame lists
 * a far-side and a near-side leg (drawn behind/in-front respectively).
 *
 * Frames:
 *   0 - "reach"      front extended forward, back gathered under body
 *   1 - "push"       mid-stride, all legs in transit
 *   2 - "suspended"  legs tucked tight under body (max airtime)
 *   3 - "recover"    front gathering, back fully extended behind
 *
 * Coordinates are local to the horse-sprite offscreen canvas centre.
 *
 * Each leg is encoded as a 6-tuple [hipX, hipY, kneeX, kneeY, footX, footY]
 * to keep the data table compact and the runtime free of object allocations.
 */

export type LegTuple = readonly [number, number, number, number, number, number];

export interface LegSegments {
  hipX: number; hipY: number;
  kneeX: number; kneeY: number;
  footX: number; footY: number;
}

export interface LegPair { far: LegSegments; near: LegSegments; }

function seg(t: LegTuple): LegSegments {
  return { hipX: t[0], hipY: t[1], kneeX: t[2], kneeY: t[3], footX: t[4], footY: t[5] };
}

export const FRONT_LEG_FRAMES: ReadonlyArray<LegPair> = [
  // 0 reach
  { far: seg([22, 14, 32, 32, 42, 46]), near: seg([18, 14, 26, 28, 36, 44]) },
  // 1 push
  { far: seg([22, 14, 28, 28, 24, 44]), near: seg([18, 14, 22, 26, 18, 42]) },
  // 2 suspended
  { far: seg([22, 12, 24, 24, 18, 30]), near: seg([18, 12, 20, 22, 14, 28]) },
  // 3 recover
  { far: seg([22, 14, 18, 30, 6, 44]), near: seg([18, 14, 14, 28, 2, 42]) },
];

export const BACK_LEG_FRAMES: ReadonlyArray<LegPair> = [
  // 0 reach
  { far: seg([-26, 14, -22, 30, -14, 44]), near: seg([-30, 14, -28, 28, -22, 42]) },
  // 1 push
  { far: seg([-26, 14, -32, 30, -38, 44]), near: seg([-30, 14, -38, 28, -46, 42]) },
  // 2 suspended
  { far: seg([-26, 12, -22, 24, -16, 30]), near: seg([-30, 12, -28, 22, -22, 28]) },
  // 3 recover
  { far: seg([-26, 14, -36, 32, -50, 46]), near: seg([-30, 14, -42, 30, -58, 44]) },
];
